import React, { useEffect } from "react";
import { FaUserShield, FaComments, FaLeaf } from "react-icons/fa";
import decorative from "../../assets/jainlogos.png";

/* ─── Injected CSS ────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600&display=swap');

.jbas {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(135deg, #1A0505 0%, #3D0A0A 50%, #5A1010 100%);
  border: 1px solid rgba(201,150,42,0.3);
  font-family: 'Inter', sans-serif;
}

/* ── gold top bar ── */
.jbas::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, #C9962A 30%, #E8C46A 50%, #C9962A 70%, transparent);
  z-index: 2;
}

/* ── mandala watermark ── */
.jbas-mandala {
  position: absolute;
  right: -60px;
  top: 50%;
  transform: translateY(-50%);
  width: min(55%, 380px);
  aspect-ratio: 1;
  opacity: 0.055;
  pointer-events: none;
  animation: jbasSpin 80s linear infinite;
}
@keyframes jbasSpin { to { transform: translateY(-50%) rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .jbas-mandala { animation: none; } }

/* ── dust ── */
.jbas-dust {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle, rgba(201,150,42,0.14) 1px, transparent 1px);
  background-size: 36px 36px;
  opacity: 1;
  pointer-events: none;
}

/* ── inner layout ── */
.jbas-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: clamp(24px, 4vw, 48px);
  padding: clamp(28px, 5vw, 52px) clamp(20px, 5vw, 52px);
}

/* ── left ── */
.jbas-left {
  flex: 1 1 55%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 2vw, 20px);
}

/* brand row */
.jbas-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.jbas-brand-logo {
  width: clamp(36px, 5vw, 48px);
  height: clamp(36px, 5vw, 48px);
  border-radius: 50%;
  border: 1.5px solid #C9962A;
  object-fit: cover;
  flex-shrink: 0;
}
.jbas-brand-name {
  font-family: 'Playfair Display', serif;
  font-size: clamp(13px, 1.5vw, 16px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.25;
}
.jbas-brand-sub {
  font-size: clamp(10px, 1.1vw, 12px);
  color: #C9962A;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 500;
}

/* headline */
.jbas-headline {
  font-family: 'Playfair Display', serif;
  font-size: clamp(22px, 3.5vw, 38px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.22;
  letter-spacing: -0.3px;
}
.jbas-headline em {
  font-style: italic;
  color: #E8C46A;
}

/* body text */
.jbas-body {
  font-size: clamp(13px, 1.4vw, 15px);
  color: rgba(253,246,236,0.75);
  line-height: 1.65;
  max-width: 440px;
}

/* divider */
.jbas-divrow {
  display: flex;
  align-items: center;
  gap: 10px;
}
.jbas-divline { flex: 0 0 40px; height: 1px; background: rgba(201,150,42,0.4); }
.jbas-divsym  { color: #C9962A; font-size: 14px; opacity: 0.7; }

/* features */
.jbas-features {
  display: flex;
  gap: clamp(12px, 2vw, 24px);
  flex-wrap: wrap;
}
.jbas-feat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 72px;
  max-width: 120px;
}
.jbas-feat-icon {
  width: clamp(44px, 5.5vw, 54px);
  height: clamp(44px, 5.5vw, 54px);
  border-radius: 50%;
  background: rgba(201,150,42,0.12);
  border: 1px solid rgba(201,150,42,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #C9962A;
  font-size: clamp(18px, 2.2vw, 22px);
  transition: background 0.2s;
}
.jbas-feat-icon:hover { background: rgba(201,150,42,0.22); }
.jbas-feat-label {
  font-size: clamp(10.5px, 1.1vw, 12px);
  color: rgba(253,246,236,0.78);
  font-weight: 500;
  text-align: center;
  line-height: 1.35;
}

/* CTA */
.jbas-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: clamp(9px,1.3vw,12px) clamp(22px,2.8vw,32px);
  background: linear-gradient(90deg, #C9962A, #A87820);
  border: none;
  border-radius: 28px;
  color: #2C1A0E;
  font-size: clamp(13px, 1.4vw, 15px);
  font-weight: 700;
  font-family: 'Playfair Display', serif;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 4px 18px rgba(201,150,42,0.3);
  transition: opacity 0.18s, transform 0.15s;
  align-self: flex-start;
}
.jbas-cta:hover { opacity: 0.88; transform: translateY(-1px); }

/* ── right panel (stat cards) ── */
.jbas-right {
  flex: 0 0 38%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.jbas-stat {
  background: rgba(201,150,42,0.08);
  border: 1px solid rgba(201,150,42,0.22);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.jbas-stat-num {
  font-family: 'Playfair Display', serif;
  font-size: clamp(24px, 2.8vw, 32px);
  font-weight: 700;
  color: #E8C46A;
  line-height: 1;
  white-space: nowrap;
}
.jbas-stat-label {
  font-size: clamp(11px, 1.2vw, 13px);
  color: rgba(253,246,236,0.7);
  line-height: 1.4;
  font-weight: 500;
}
.jbas-stat-icon {
  font-size: 20px;
  color: #C9962A;
  opacity: 0.6;
  flex-shrink: 0;
}

/* ════════════════════
   RESPONSIVE
════════════════════ */

/* Tablet → hide right stats, compress */
@media (max-width: 900px) {
  .jbas-right { display: none; }
  .jbas-left  { flex: 1 1 100%; }
}

/* Mobile */
@media (max-width: 600px) {
  .jbas-inner { flex-direction: column; align-items: flex-start; }
  .jbas-features { gap: 10px; }
  .jbas-feat  { min-width: 64px; }
  .jbas-cta   { align-self: stretch; justify-content: center; }
  .jbas-mandala { right: -80px; width: 70%; opacity: 0.04; }
}

/* Very small */
@media (max-width: 360px) {
  .jbas-features { flex-wrap: wrap; }
  .jbas-feat { flex: 0 0 calc(33% - 8px); }
}
`;

/* ─── Mandala SVG ─────────────────────────────────────────────────────────── */
const Mandala = () => (
  <svg
    className="jbas-mandala"
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {[30, 60, 90, 120, 150, 175, 195].map((r, i) => (
      <circle
        key={i}
        cx="200"
        cy="200"
        r={r}
        stroke="#C9962A"
        strokeWidth={i === 6 ? 1.5 : 0.8}
        strokeDasharray={i === 1 ? "4 4" : undefined}
      />
    ))}
    {Array.from({ length: 16 }, (_, i) => {
      const a = i * 22.5 * (Math.PI / 180);
      const x1 = 200 + 90 * Math.cos(a),
        y1 = 200 + 90 * Math.sin(a);
      const cx1 = 200 + 130 * Math.cos(a + 0.18),
        cy1 = 200 + 130 * Math.sin(a + 0.18);
      const cx2 = 200 + 130 * Math.cos(a - 0.18),
        cy2 = 200 + 130 * Math.sin(a - 0.18);
      return (
        <path
          key={i}
          d={`M200,200 Q${cx1},${cy1} ${x1},${y1} Q${cx2},${cy2} 200,200`}
          stroke="#C9962A"
          strokeWidth="0.7"
        />
      );
    })}
    {Array.from({ length: 12 }, (_, i) => {
      const a = i * 30 * (Math.PI / 180);
      return (
        <line
          key={i}
          x1={200 + 22 * Math.cos(a)}
          y1={200 + 22 * Math.sin(a)}
          x2={200 + 62 * Math.cos(a)}
          y2={200 + 62 * Math.sin(a)}
          stroke="#C9962A"
          strokeWidth="0.8"
        />
      );
    })}
    <circle cx="200" cy="200" r="22" stroke="#C9962A" strokeWidth="1" />
    {Array.from({ length: 8 }, (_, i) => {
      const a = (i * 45 + 22.5) * (Math.PI / 180);
      return (
        <circle
          key={i}
          cx={200 + 168 * Math.cos(a)}
          cy={200 + 168 * Math.sin(a)}
          r="7"
          stroke="#C9962A"
          strokeWidth="0.6"
        />
      );
    })}
  </svg>
);

/* ─── Features data ───────────────────────────────────────────────────────── */
const features = [
  { Icon: FaUserShield, label: "Verified Jain Profiles" },
  { Icon: FaComments, label: "Dedicated Relationship Manager" },
  { Icon: FaLeaf, label: "Value-Aligned Matches" },
];

const stats = [
  { num: "10×", label: "Faster sacred matchmaking" },
  { num: "5000+", label: "Jain families connected" },
  { num: "98%", label: "Family satisfaction rate" },
];

/* ─── Component ───────────────────────────────────────────────────────────── */
const AssistedService = () => {
  useEffect(() => {
    const id = "jbas-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="jbas">
      {/* Rotating mandala */}
      <Mandala />
      {/* Gold dust grid */}
      <div className="jbas-dust" aria-hidden="true" />

      <div className="jbas-inner">
        {/* ── LEFT ── */}
        <div className="jbas-left">
          {/* Brand row */}
          <div className="jbas-brand">
            <img
              src={decorative}
              alt="Jain Var Vadhu"
              className="jbas-brand-logo"
            />
            <div>
              <div className="jbas-brand-name">Jain Var Vadhu</div>
              <div className="jbas-brand-sub">Personalised Matchmaking</div>
            </div>
          </div>

          {/* Headline */}
          <h2 className="jbas-headline">
            Find your <em>sacred</em> match
            <br />
            with divine guidance
          </h2>

          {/* Body */}
          <p className="jbas-body">
            Our expert Relationship Managers walk with you on the path of Dharma
            — carefully curating profiles that align with your values, family
            traditions, and spiritual principles.
          </p>

          {/* Divider */}
          <div className="jbas-divrow" aria-hidden="true">
            <div className="jbas-divline" />
            <span className="jbas-divsym">☸</span>
            <div className="jbas-divline" />
          </div>

          {/* Feature icons */}
          <div
            className="jbas-features"
            role="list"
            aria-label="Service features"
          >
            {features.map(({ Icon, label }) => (
              <div key={label} className="jbas-feat" role="listitem">
                <div className="jbas-feat-icon" aria-hidden="true">
                  <Icon />
                </div>
                <span className="jbas-feat-label">{label}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a href="/#register-form" className="jbas-cta">
            🪷 Know More
          </a>
        </div>

        {/* ── RIGHT — stat cards ── */}
        <div className="jbas-right" aria-label="Platform statistics">
          {stats.map(({ num, label }) => (
            <div key={label} className="jbas-stat">
              <div className="jbas-stat-num">{num}</div>
              <div className="jbas-stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssistedService;
